import { Injectable } from '@angular/core';
import { WHBABTEC_ENVIRONMENT } from '@whbabtec-environment';
import { WhbabtecUser, mapClaimsToUser } from '@whbabtec-types';
import { AuthConfig, OAuthService } from 'angular-oauth2-oidc';
import { BehaviorSubject, Observable } from 'rxjs';
import { LanguageService } from './language.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private user$$ = new BehaviorSubject<WhbabtecUser | undefined>(undefined);

  private readonly allowedIssuers = WHBABTEC_ENVIRONMENT.auth.issuers;

  private initPromise: Promise<void> | null = null;

  /**
   * Constructs the Azure CIAM logout URL from the issuer.
   * The issuer format is: https://{tenant}.ciamlogin.com/{tenantId}/v2.0
   * The logout URL should be: https://{tenant}.ciamlogin.com/{tenantId}/oauth2/v2.0/logout
   */
  private getLogoutUrl(): string {
    const issuer = WHBABTEC_ENVIRONMENT.auth.issuers[0];
    const baseUrl = issuer.replace(/\/v2\.0$/, '');
    return `${baseUrl}/oauth2/v2.0/logout`;
  }

  private readonly config: AuthConfig = {
    clientId: WHBABTEC_ENVIRONMENT.auth.clientId,
    redirectUri: window.location.origin + '/signin-oidc',
    postLogoutRedirectUri: window.location.origin,
    scope: WHBABTEC_ENVIRONMENT.auth.scopes.join(' '),
    issuer: WHBABTEC_ENVIRONMENT.auth.issuers[0],
    skipIssuerCheck: true,
    logoutUrl: this.getLogoutUrl(),
    responseType: 'code',
    tokenEndpoint: `${WHBABTEC_ENVIRONMENT.api.baseUrl}/api/v1/login/callback`,
    silentRefreshRedirectUri:
      window.location.origin + '/assets/oauth/silent-refresh.html',
    oidc: true,
    strictDiscoveryDocumentValidation: false,
    useSilentRefresh: false,
  };

  constructor(
    private oauthService: OAuthService,
    private languageService: LanguageService
  ) {}

  get user$(): Observable<WhbabtecUser | undefined> {
    return this.user$$.asObservable();
  }

  public init(): Promise<void> {
    if (!this.initPromise) {
      this.initPromise = this.performInit();
    }
    return this.initPromise;
  }

  private async performInit(): Promise<void> {
    const lang = this.languageService.getMappedLanguage();
    this.config.customQueryParams = {
      ui_locales: lang,
      mkt: lang,
    };

    this.oauthService.configure(this.config);
    this.oauthService.tokenEndpoint = this.config.tokenEndpoint;
    await this.oauthService.loadDiscoveryDocument();
    this.oauthService.tokenEndpoint = this.config.tokenEndpoint;

    await this.oauthService.tryLogin();

    if (
      this.oauthService.hasValidAccessToken() &&
      this.hasValidTokenIssuer()
    ) {
      this.refreshUser();
    }
  }

  private hasValidTokenIssuer(): boolean {
    const claims = this.oauthService.getIdentityClaims();
    if (!claims || !claims['iss']) {
      return false;
    }

    const tokenIssuer = claims['iss'] as string;
    const isValid = this.allowedIssuers.some(
      (issuer) => tokenIssuer === issuer
    );

    if (!isValid) {
      console.error(
        `Token issuer '${tokenIssuer}' is not in the list of allowed issuers.`
      );
      this.oauthService.logOut();
    }

    return isValid;
  }

  public login(): void {
    this.oauthService.initCodeFlow();
  }

  public async fetchToken(): Promise<void> {
    await this.init();
  }

  public async tryRefreshToken(): Promise<boolean> {
    try {
      const refreshToken = this.oauthService.getRefreshToken();
      if (!refreshToken) {
        return false;
      }

      await this.oauthService.refreshToken();
      if (this.oauthService.hasValidAccessToken()) {
        this.refreshUser();
        return true;
      }
      return false;
    } catch (error) {
      console.warn('Token refresh failed:', error);
      return false;
    }
  }

  public logout(): void {
    this.oauthService.logOut();
    this.user$$.next(undefined);
  }

  public isLoggedIn(): boolean {
    return this.oauthService.hasValidAccessToken();
  }

  private refreshUser(): void {
    const user = this.getUser();
    if (user) {
      this.user$$.next(user);
    }
  }

  public getToken(): string | null {
    if (this.oauthService.hasValidIdToken()) {
      return this.oauthService.getIdToken();
    }
    return null;
  }

  public hasRefreshToken(): boolean {
    return !!this.oauthService.getRefreshToken();
  }

  public getUser(): WhbabtecUser | null {
    if (!this.oauthService.hasValidAccessToken()) {
      return null;
    }

    const claims = this.oauthService.getIdentityClaims();
    if (!claims) {
      return null;
    }

    return mapClaimsToUser(claims);
  }
}
